import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Camera, Images, Upload, TrendingUp, History, LogOut, Delete, X, Lock, Landmark, QrCode } from "lucide-react";
import { QrScanner, QrPreview, scanImageFiles } from "@/components/QrScan";
import { papi } from "@/lib/portalApi";
import { VatReportsTab, VatAlerts, ReportSheet } from "@/components/PortalVatReports";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge, Money } from "@/components/common";
import DateInput from "@/components/DateInput";
import { UploadImages } from "@/components/PendingUploads";
import { rangeFor } from "@/components/PeriodBar";
import { salesVat } from "@/pages/SalesEntry";
import { formatErr } from "@/lib/api";
import { InstallApp, usePortalPwa } from "@/components/InstallApp";
import { fmtDate, fmtDateTime, fmtMoney, riyadhToday } from "@/lib/format";


const PinPad = ({ info, onLogin }) => {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (p) => {
    setBusy(true); setErr("");
    try { await onLogin(p); } catch (e) { setErr(formatErr(e)); setPin(""); } finally { setBusy(false); }
  };
  const press = (d) => { const p = (pin + d).slice(0, 8); setPin(p); };
  return (
    <div className="min-h-screen flex flex-col bg-[#064E3B] text-white" data-testid="portal-login">
      <div className="flex-1 flex flex-col items-center justify-center px-6 pt-12">
        <div className="w-14 h-14 rounded-2xl bg-amber-500 text-[#064E3B] flex items-center justify-center text-2xl font-bold mb-4">﷼</div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-300">{info.app_name}</div>
        <h1 className="text-2xl font-semibold mt-1 text-center" data-testid="portal-company-name">{info.company_name}</h1>
        <div className="mt-6 flex items-center gap-2 text-emerald-200 text-sm"><Lock className="w-4 h-4" />Enter your PIN</div>
        <div className="flex gap-3 mt-4 h-4" data-testid="portal-pin-dots">{Array.from({ length: Math.max(4, pin.length) }).map((_, i) => <span key={i} className={`w-3.5 h-3.5 rounded-full ${i < pin.length ? "bg-amber-400" : "bg-white/20"}`} />)}</div>
        <div className="mt-5"><InstallApp dark /></div>
        {err && <div className="mt-4 text-sm text-red-200 bg-red-500/20 rounded-lg px-3 py-1.5" data-testid="portal-login-error">{err}</div>}
      </div>
      <div className="grid grid-cols-3 gap-3 px-8 pb-10 pt-6 max-w-sm w-full mx-auto">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => <button key={d} onClick={() => press(d)} className="h-16 rounded-2xl bg-white/10 active:bg-white/25 text-2xl font-medium" data-testid={`pin-key-${d}`}>{d}</button>)}
        <button onClick={() => setPin(pin.slice(0, -1))} className="h-16 rounded-2xl flex items-center justify-center active:bg-white/10" data-testid="pin-key-del"><Delete className="w-6 h-6" /></button>
        <button onClick={() => press("0")} className="h-16 rounded-2xl bg-white/10 active:bg-white/25 text-2xl font-medium" data-testid="pin-key-0">0</button>
        <button disabled={pin.length < 4 || busy} onClick={() => go(pin)} className="h-16 rounded-2xl bg-amber-500 text-[#064E3B] font-semibold disabled:opacity-40" data-testid="pin-key-enter">{busy ? "…" : "Enter"}</button>
      </div>
    </div>
  );
};

const UploadTab = ({ onDone }) => {
  const [date, setDate] = useState(riyadhToday());
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState(undefined);
  const [scanning, setScanning] = useState(false);
  const [live, setLive] = useState(false);
  const cam = useRef(null);
  const gal = useRef(null);
  const applyQr = (z) => {
    setQr(z);
    if (z?.invoice_date && z.invoice_date <= riyadhToday()) { setDate(z.invoice_date); } else if (!z) { setDate(riyadhToday()); }
  };
  const onLive = useCallback((z) => { setLive(false); applyQr({ ...z, origin: "scan" }); toast.success("E-invoice QR read"); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const add = (list) => {
    const picked = Array.from(list).map((file) => ({ file, url: URL.createObjectURL(file) }));
    setFiles((f) => [...f, ...picked].slice(0, 10));
    if (!qr) { setScanning(true); scanImageFiles(picked.map((p) => p.file)).then(applyQr).finally(() => setScanning(false)); }
  };
  const send = async () => {
    const fd = new FormData();
    fd.append("bill_date", date); fd.append("note", note);
    if (qr?.text) { fd.append("qr_text", qr.text); fd.append("qr_origin", qr.origin || "scan"); }
    files.forEach((f) => fd.append("files", f.file));
    setBusy(true);
    try { await papi.post("/portal/uploads", fd); toast.success("Bill uploaded"); setFiles([]); setNote(""); setQr(undefined); setDate(riyadhToday()); onDone(); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="portal-upload-tab">
      <div className="bg-white rounded-2xl p-4 shadow-sm space-y-3">
        <div className="text-xs font-medium text-slate-500">Bill date</div>
        <DateInput value={date} max={riyadhToday()} onChange={setDate} className="h-12 text-base" testid="portal-bill-date" />
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="h-12" data-testid="portal-bill-note" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => cam.current.click()} className="bg-emerald-800 text-white rounded-2xl py-6 flex flex-col items-center gap-2 active:scale-95 transition-transform" data-testid="portal-camera-button"><Camera className="w-7 h-7" /><span className="font-medium">Take photo</span></button>
        <button onClick={() => gal.current.click()} className="bg-white text-emerald-900 border border-emerald-200 rounded-2xl py-6 flex flex-col items-center gap-2 active:scale-95 transition-transform" data-testid="portal-gallery-button"><Images className="w-7 h-7" /><span className="font-medium">From gallery</span></button>
        <input ref={cam} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="portal-camera-input" />
        <input ref={gal} type="file" accept="image/*" multiple hidden onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="portal-gallery-input" />
      </div>
      <button onClick={() => setLive(true)} className="w-full bg-white border border-emerald-200 rounded-2xl py-4 flex items-center justify-center gap-2 text-emerald-900 font-medium active:scale-95 transition-transform shadow-sm" data-testid="portal-scan-qr-button"><QrCode className="w-5 h-5" />Scan invoice QR (ZATCA)</button>
      <QrPreview qr={qr} scanning={scanning} onClear={() => { setQr(undefined); setDate(riyadhToday()); }} />
      {live && <QrScanner onResult={onLive} onClose={() => setLive(false)} />}
      {files.length > 0 && (
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="text-xs text-slate-500 mb-2">{files.length} page(s) — uploaded together as one bill</div>
          <div className="grid grid-cols-3 gap-2">
            {files.map((f, i) => (
              <div key={f.url} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100">
                <img src={f.url} alt="" className="w-full h-full object-cover" />
                <button onClick={() => { const rest = files.filter((_, j) => j !== i); setFiles(rest); if (qr?.origin === "photo" && qr.file === f.file) { setQr(undefined); setDate(riyadhToday()); if (rest.length) { setScanning(true); scanImageFiles(rest.map((p) => p.file)).then(applyQr).finally(() => setScanning(false)); } } }} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center" data-testid={`portal-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
          <Button disabled={busy || !date} onClick={send} className="w-full h-12 mt-4 rounded-xl bg-amber-500 hover:bg-amber-600 text-[#064E3B] font-semibold" data-testid="portal-upload-submit"><Upload className="w-4 h-4 mr-2" />{busy ? "Uploading…" : "Upload bill"}</Button>
        </div>
      )}
    </div>
  );
};

const SalesTab = ({ rate, onDone }) => {
  const t = riyadhToday();
  const [f, setF] = useState({ period_from: t, period_to: t, total_sales: "" });
  const [busy, setBusy] = useState(false);
  const { vat, net } = salesVat(f.total_sales, rate);
  const save = async () => {
    setBusy(true);
    try { await papi.post("/portal/sales", { ...f, total_sales: Number(f.total_sales) }); toast.success("Sales submitted"); setF({ ...f, total_sales: "" }); onDone(); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm space-y-4" data-testid="portal-sales-tab">
      <div className="grid grid-cols-2 gap-3">
        <div><div className="text-xs text-slate-500 mb-1">From date</div><DateInput value={f.period_from} onChange={(v) => setF({ ...f, period_from: v })} className="h-12" testid="portal-sales-from" /></div>
        <div><div className="text-xs text-slate-500 mb-1">To date</div><DateInput value={f.period_to} onChange={(v) => setF({ ...f, period_to: v })} className="h-12" testid="portal-sales-to" /></div>
      </div>
      <div><div className="text-xs text-slate-500 mb-1">Total sales (incl. VAT) · SAR</div>
        <Input type="number" inputMode="decimal" value={f.total_sales} onChange={(e) => setF({ ...f, total_sales: e.target.value })} className="h-14 text-2xl num text-right" placeholder="0.00" data-testid="portal-sales-total" /></div>
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex justify-between text-sm"><span className="text-amber-800">VAT included ({rate}%)</span><span className="num font-semibold" data-testid="portal-sales-vat">{fmtMoney(vat)}</span></div>
      <div className="flex justify-between text-sm px-1"><span className="text-slate-500">Net sales</span><span className="num">{fmtMoney(net)}</span></div>
      <Button disabled={busy || !(Number(f.total_sales) > 0) || f.period_from > f.period_to} onClick={save} className="w-full h-12 rounded-xl bg-emerald-800 hover:bg-emerald-900" data-testid="portal-sales-submit">{busy ? "Saving…" : "Save sales"}</Button>
    </div>
  );
};

const HistoryTab = ({ tick }) => {
  const [kind, setKind] = useState("bills");
  const [range, setRange] = useState(rangeFor("month"));
  const [rows, setRows] = useState([]);
  const [view, setView] = useState(null);
  useEffect(() => { setRows([]); }, [kind]);
  const reqId = useRef(0);
  const load = useCallback(() => {
    const me = ++reqId.current;
    setRows([]);
    papi.get(kind === "bills" ? "/portal/uploads" : "/portal/sales", { params: { date_from: range[0], date_to: range[1] } })
      .then(({ data }) => { if (me === reqId.current) setRows(data); }).catch(() => {});
  }, [kind, range]);
  useEffect(() => { load(); }, [load, tick]);
  const tone = (s) => s;
  return (
    <div className="space-y-3" data-testid="portal-history-tab">
      <div className="grid grid-cols-2 bg-white rounded-xl p-1 shadow-sm">
        {[["bills", "Purchase bills"], ["sales", "Sales"]].map(([k, l]) => <button key={k} onClick={() => setKind(k)} className={`py-2 rounded-lg text-sm font-medium ${kind === k ? "bg-emerald-800 text-white" : "text-slate-600"}`} data-testid={`portal-history-${k}`}>{l}</button>)}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <DateInput value={range[0]} onChange={(v) => setRange([v, range[1]])} className="h-10 bg-white" testid="portal-history-from" />
        <DateInput value={range[1]} onChange={(v) => setRange([range[0], v])} className="h-10 bg-white" testid="portal-history-to" />
      </div>
      {!rows.length && <div className="text-center text-sm text-slate-400 py-10">No records in this period</div>}
      {kind === "bills" ? rows.map((u, i) => (
        <div key={u.id} className="bg-white rounded-2xl p-3 shadow-sm" data-testid={`portal-upload-row-${i}`}>
          <div className="flex justify-between items-center mb-2"><div><div className="font-medium num">{fmtDate(u.bill_date)}</div><div className="text-[11px] text-slate-400 num">{u.upload_no} · {fmtDateTime(u.created_at)}</div></div><StatusBadge status={tone(u.status)} /></div>
          {u.status === "rejected" && <div className="text-xs text-red-700 mb-2">Rejected: {u.reject_reason}</div>}
          {u.bill_record_no && <div className="text-xs text-emerald-700 mb-2 num">Recorded as {u.bill_record_no}</div>}
          <div className="flex gap-2 overflow-x-auto">{(u.images || []).map((im) => <button key={im.thumb_path} onClick={() => setView(u)}><img src={im.thumb_url} alt="" className="w-20 h-20 rounded-lg object-cover" /></button>)}</div>
        </div>
      )) : rows.map((s, i) => (
        <div key={s.id} className="bg-white rounded-2xl p-3 shadow-sm" data-testid={`portal-sales-row-${i}`}>
          <div className="flex justify-between items-center"><div className="num font-medium text-sm">{fmtDate(s.period_from)} – {fmtDate(s.period_to)}</div><StatusBadge status={tone(s.status)} /></div>
          <div className="flex justify-between text-sm mt-1"><Money value={s.total_sales} strong /><span className="text-xs text-slate-500">VAT {fmtMoney(s.vat_amount)}</span></div>
          {s.status === "rejected" && <div className="text-xs text-red-700 mt-1">Rejected: {s.reject_reason}</div>}
        </div>
      ))}
      {view && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="portal-image-viewer">
          <div className="flex justify-between items-center p-3 text-white"><span className="num text-sm">{view.upload_no} · {fmtDate(view.bill_date)}</span><button onClick={() => setView(null)} data-testid="portal-viewer-close"><X className="w-6 h-6" /></button></div>
          <div className="flex-1 p-2"><UploadImages upload={view} className="h-[78vh]" testid="portal-zoom" /></div>
        </div>
      )}
    </div>
  );
};

export default function Portal() {
  const { code } = useParams();
  const key = `ksavat_portal_${code}`;
  const [info, setInfo] = useState(null);
  const [bad, setBad] = useState("");
  const [sess, setSess] = useState(() => { try { return JSON.parse(localStorage.getItem(key)); } catch (e) { return null; } });
  const [tab, setTab] = useState("upload");
  const [tick, setTick] = useState(0);
  const [pending, setPending] = useState([]);
  const [report, setReport] = useState(null);
  const closeReport = useCallback(() => setReport(null), []);

  useEffect(() => { axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/portal/${code}/info`).then(({ data }) => setInfo(data)).catch((e) => setBad(formatErr(e))); }, [code]);
  usePortalPwa(code, info?.company_name);
  const logout = useCallback(() => { localStorage.removeItem(key); setSess(null); }, [key]);
  useEffect(() => {
    papi.defaults.headers.common.Authorization = sess ? `Bearer ${sess.token}` : "";
    const id = papi.interceptors.response.use((r) => r, (e) => { if (e.response?.status === 401) logout(); return Promise.reject(e); });
    const t = setInterval(() => { if (sess && new Date(sess.expires_at) < new Date()) logout(); }, 15000);
    return () => { papi.interceptors.response.eject(id); clearInterval(t); };
  }, [sess, logout]);
  useEffect(() => {
    if (!sess) { return; }
    papi.get("/portal/vat-reports", { params: { limit: 1 } }).then(({ data }) => setPending(data.pending)).catch(() => {});
  }, [sess, tick]);

  if (bad) return <div className="min-h-screen flex items-center justify-center p-6 text-center text-slate-600" data-testid="portal-invalid">{bad}</div>;
  if (!info) return null;
  const valid = sess && new Date(sess.expires_at) > new Date();
  if (!valid) return <PinPad info={info} onLogin={async (pin) => { const { data } = await papi.post(`/portal/${code}/login`, { pin }); localStorage.setItem(key, JSON.stringify(data)); setSess(data); }} />;

  const TABS = [["upload", "Upload bill", Upload], ["sales", "Sales", TrendingUp], ["vat", "VAT Reports", Landmark], ["history", "History", History]];
  return (
    <div className="min-h-screen bg-slate-100 pb-24" data-testid="portal-app">
      <header className="sticky top-0 z-30 bg-[#064E3B] text-white px-5 pt-[calc(1.25rem+env(safe-area-inset-top))] pb-4 rounded-b-3xl shadow">
        <div className="flex justify-between items-start">
          <div><div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300">{info.app_name}</div><div className="font-semibold text-lg leading-tight">{info.company_name}</div></div>
          <button onClick={logout} className="p-2 -mr-2 rounded-full active:bg-white/10" data-testid="portal-logout"><LogOut className="w-5 h-5" /></button>
        </div>
        <div className="flex justify-between items-center mt-1 gap-2">
          <div className="text-[11px] text-emerald-200 num">Session ends {fmtDateTime(sess.expires_at).slice(11)}</div>
          <InstallApp dark />
        </div>
      </header>
      <main className="max-w-md mx-auto px-4 pt-4">
        {tab !== "vat" && pending.length > 0 && <VatAlerts pending={pending} onOpen={setReport} onAll={() => setTab("vat")} />}
        {tab === "vat" && <VatReportsTab tick={tick} onOpen={setReport} />}
        {tab === "upload" && <UploadTab onDone={() => setTick(tick + 1)} />}
        {tab === "sales" && <SalesTab rate={Number(info.vat_rate)} onDone={() => setTick(tick + 1)} />}
        {tab === "history" && <HistoryTab tick={tick} />}
      </main>
      <nav className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 grid grid-cols-4 pb-[env(safe-area-inset-bottom)]">
        {TABS.map(([k, l, I]) => (
          <button key={k} onClick={() => setTab(k)} className={`py-3 flex flex-col items-center gap-1 text-[11px] font-medium ${tab === k ? "text-emerald-800" : "text-slate-400"}`} data-testid={`portal-tab-${k}`}>
            <span className="relative"><I className="w-5 h-5" />{k === "vat" && pending.length > 0 && <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] leading-4 text-center num" data-testid="portal-vat-badge">{pending.length}</span>}</span>{l}
          </button>
        ))}
      </nav>
      {report && <ReportSheet id={report} onClose={closeReport} onChanged={() => setTick((x) => x + 1)} />}
    </div>
  );
}
